import {isEqual, omit} from 'lodash';
import {DateTime} from 'luxon';

import {CleanHtml} from '../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {CalendarEventVersionAttachmentModel} from '../../../libs/shared/core/src/models/api/attachmentModel';
import {ChannelTypesEnum} from '../../../libs/shared/core/src/models/api/channelCommonTypes';
import {PaletteColorsEnum} from '../../../libs/shared/core/src/models/api/paletteModel';
import {isProxyOrganizer} from '../../../src/helpers/models/calendarEventHelpers';
import {
  CalendarEventVersionModel,
  CalendarEventVersionShowAsTypesEnum,
  CalendarEventVersionVisibilityTypesEnum,
} from '../../../src/models/api/calendarEventVersionModel';
import {CalendarVersionModel} from '../../../src/models/api/calendarVersionModel';
import {ConferenceModel} from '../../../src/models/api/conferenceModel';
import {ParticipantTypesEnum} from '../../../src/models/api/participantModel';
import {RecurrenceModel} from '../../../src/models/api/recurrenceModel';
import {VisualParticipantModel} from '../../../src/models/api/visualParticipantModel';
import {buildCalendarEventDescriptionHtml} from '../../helpers/calendarEventHelpers';
import {isStartEndDataEqual, StartEndData} from '../../helpers/calendarHelpers';
import {
  buildCalendarEventVersionLocationText,
  findCalendarEventVersionColor,
} from '../../helpers/models/calendarEventVersionHelpers';

const personalFields = ['color', 'visibility', 'showAs', 'reminders'];

// FE model representing the data stored in the calendar event editor.
export interface CalendarEventEditorDataModel {
  color: PaletteColorsEnum;
  description: CleanHtml | undefined;
  summary: string;
  location: string;
  showAs: CalendarEventVersionShowAsTypesEnum;
  visibility: CalendarEventVersionVisibilityTypesEnum;
  conference: ConferenceModel | undefined;
  isInstantReschedulingEnabled: boolean;
  reminders: any;
  recurrence: RecurrenceModel | undefined;
  startEndData: StartEndData;
  nonResourceParticipants: ReadonlyArray<VisualParticipantModel>;
  resourceParticipants: ReadonlyArray<VisualParticipantModel>;
  attachments: ReadonlyArray<CalendarEventVersionAttachmentModel>;
}

export function makeCalendarEventEditorDataModel(
  calendarEventVersion: CalendarEventVersionModel,
  calendarVersion: CalendarVersionModel,
  isInstantReschedulingEnabled: boolean,
  channelType: ChannelTypesEnum | undefined,
  startDateTime: DateTime,
  endDateTime: DateTime,
  visualParticipants: ReadonlyArray<VisualParticipantModel>,
): CalendarEventEditorDataModel {
  const startEndData = {
    startDateTime,
    endDateTime,
    isAllDay: calendarEventVersion.isAllDay,
  };
  const nonResourceParticipants = visualParticipants.filter((p) => p.type !== ParticipantTypesEnum.RESOURCE);
  const resourceParticipants = visualParticipants.filter((p) => p.type === ParticipantTypesEnum.RESOURCE);

  return {
    color: findCalendarEventVersionColor(calendarEventVersion, calendarVersion),
    description: buildCalendarEventDescriptionHtml(calendarEventVersion.description, channelType),
    summary: calendarEventVersion.summary || '',
    location: buildCalendarEventVersionLocationText(calendarEventVersion),
    showAs: calendarEventVersion.showAs,
    visibility: calendarEventVersion.visibility,
    conference: calendarEventVersion.conference,
    isInstantReschedulingEnabled,
    reminders: Array.isArray(calendarEventVersion.reminders)
      ? calendarEventVersion.reminders
      : calendarVersion.reminders,
    recurrence: calendarEventVersion.recurrence,
    startEndData,
    nonResourceParticipants,
    resourceParticipants,
    attachments: calendarEventVersion.attachments || [],
  };
}

// For tracking changes to CalendarEventEditorModel.
export interface CalendarEventEditorModelChanges {
  isSaveEnabled: boolean;
  didGlobalSettingsChange: boolean;
  didRecurrenceChange: boolean;
  didStartEndDataChange: boolean;
  didGlobalFieldsChangeOmittingGuestChanges: boolean;
  guestChanges: CalendarEventEditorModelGuestChanges;
}

export interface ComputeCalendarEventEditorModelChangesParams {
  initialDataModel: CalendarEventEditorDataModel;
  currentDataModel: CalendarEventEditorDataModel;
  isUnsavedDraft: boolean;
  userContactUid: string;
}

export interface CalendarEventEditorModelGuestChanges {
  /* Number of guests that are already existing, weren't removed, and should receive updates if event
     attributes other than guests (description, time, etc.) change. */
  existingGuestCount: number;
  /* Number of guests who are removed when editing the event. */
  removedGuestCount: number;
  /* Number of guests who are added when editing the event. */
  newGuestCount: number;
  /* Number of all the event's current guests, including changes made while editing the event. */
  finalGuestCount: number;
}

export function computeCalendarEventEditorModelChanges({
  initialDataModel,
  currentDataModel,
  isUnsavedDraft,
  userContactUid,
}: ComputeCalendarEventEditorModelChangesParams): CalendarEventEditorModelChanges {
  const initialGuestContactUids = computeGuestContactUids(
    initialDataModel.nonResourceParticipants,
    userContactUid,
  );
  const finalGuestContactUids = computeGuestContactUids(
    currentDataModel.nonResourceParticipants,
    userContactUid,
  );

  const removedGuestContactUids = initialGuestContactUids.filter(
    (uid) => !finalGuestContactUids.includes(uid),
  );
  const newGuestContactUids = finalGuestContactUids.filter((uid) => !initialGuestContactUids.includes(uid));
  const existingGuestContactUids = initialGuestContactUids.filter(
    (uid) => !removedGuestContactUids.includes(uid),
  );

  const guestChanges = {
    existingGuestCount: existingGuestContactUids.length,
    removedGuestCount: removedGuestContactUids.length,
    newGuestCount: newGuestContactUids.length,
    finalGuestCount: finalGuestContactUids.length,
  };

  // We can't use lodash isEqual to compare luxon DateTime objects directly.
  const didStartEndDataChange = !isStartEndDataEqual(
    initialDataModel.startEndData,
    currentDataModel.startEndData,
  );
  const didDataModelChange =
    didStartEndDataChange ||
    !isEqual(omit(initialDataModel, 'startEndData'), omit(currentDataModel, 'startEndData'));
  const didGlobalFieldsChangeOmittingGuestChanges =
    !isEqual(
      omit(initialDataModel, [...personalFields, 'startEndData', 'nonResourceParticipants']),
      omit(currentDataModel, [...personalFields, 'startEndData', 'nonResourceParticipants']),
    ) || didStartEndDataChange;

  const isSaveEnabled = isUnsavedDraft || didDataModelChange;

  const didGlobalSettingsChange =
    initialDataModel.summary !== currentDataModel.summary ||
    initialDataModel.location !== currentDataModel.location ||
    !isStartEndDataEqual(initialDataModel.startEndData, currentDataModel.startEndData) ||
    initialDataModel.description !== currentDataModel.description;

  const didRecurrenceChange = !isEqual(initialDataModel.recurrence, currentDataModel.recurrence);

  return {
    guestChanges,
    didGlobalFieldsChangeOmittingGuestChanges,
    didGlobalSettingsChange,
    didRecurrenceChange,
    didStartEndDataChange,
    isSaveEnabled,
  };
}

// Filter out the user themself and any proxy organizer to get the array of guests.
function computeGuestContactUids(
  nonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
  userContactUid: string,
): ReadonlyArray<string> {
  return nonResourceParticipants
    .map((participant) => participant.contact.uid)
    .filter((uid) => uid !== userContactUid && !isProxyOrganizer(uid));
}
